import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Send, Search, Plus, X, Trash2, RotateCcw, Check, XCircle, Edit2 } from 'lucide-react';
import { AuthenticatedLayout } from '../layouts/AuthenticatedLayout';
import { Button } from '../components/common/Button';
import { useTheme } from '../context/ThemeContext';
import { isTransactionMessage, isErrorMessage, getCategoryColor } from '../utils/chatMessageParser';

const API_BASE = `${process.env.REACT_APP_API_URL}/chat`;

// Helper function to get auth token (it's stored as 'token' not 'access_token')
const getAuthToken = () => {
  return localStorage.getItem('token') || localStorage.getItem('access_token');
};

export const ChatInterfacePage = () => {
  const { darkMode } = useTheme();
  const location = useLocation();
  const [conversations, setConversations] = useState([]);
  const [selectedConversationId, setSelectedConversationId] = useState(null);
  const [messageInput, setMessageInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [pendingConfirmation, setPendingConfirmation] = useState(null);
  const [pendingAssistantMessage, setPendingAssistantMessage] = useState(null);
  const [conversationsLoading, setConversationsLoading] = useState(true);
  const [editingConversationId, setEditingConversationId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [deleteConfirmIsOpen, setDeleteConfirmIsOpen] = useState(false);
  const [conversationToDelete, setConversationToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const scrollRef = useRef(null);

  // Load conversations on mount
  const fetchConversations = useCallback(async () => {
    try {
      setConversationsLoading(true);
      const token = getAuthToken();
      const response = await fetch(`${API_BASE}/conversations/`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      const data = await response.json();
      setConversations(data);
      // Don't auto-select on mobile - let user choose which conversation to view
      // Desktop will handle selection via the sidebar click
    } catch (error) {
      // Failed to fetch conversations
    } finally {
      setConversationsLoading(false);
    }
  }, []);

  // Load messages when conversation changes
  const fetchMessages = useCallback(
    async (page = 1) => {
      try {
        const token = getAuthToken();
        const response = await fetch(
          `${API_BASE}/messages/?page=${page}&conversation_id=${selectedConversationId}&page_size=20`,
          {
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          }
        );
        const data = await response.json();
        setMessages(data.results || data || []);
      } catch (error) {
        // Failed to fetch messages
      }
    },
    [selectedConversationId]
  );

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Handle reset to message list from floating button on mobile
  useEffect(() => {
    if (location.state?.resetToMessageList) {
      setSelectedConversationId(null);
      setMessages([]);
      // Clear the state to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
  }, [location.state?.resetToMessageList, location.pathname]);

  // Load messages when conversation changes
  useEffect(() => {
    if (selectedConversationId) {
      fetchMessages(1);
    } else {
      setMessages([]);
    }
  }, [selectedConversationId, fetchMessages]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const updateConversationTitle = async (conversationId, newTitle) => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE}/conversations/${conversationId}/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ title: newTitle }),
      });

      if (response.ok) {
        // Update local conversations list
        setConversations((prev) =>
          prev.map((conv) =>
            conv.id === conversationId ? { ...conv, title: newTitle } : conv
          )
        );
      }
    } catch (error) {
    }
  };

  const deleteConversation = async (conversationId) => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE}/conversations/${conversationId}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        // Remove from local list
        setConversations((prev) =>
          prev.filter((conv) => conv.id !== conversationId)
        );
        
        // If the deleted conversation was selected, clear selection
        if (selectedConversationId === conversationId) {
          setSelectedConversationId(null);
          setMessages([]);
        }
      }
    } catch (error) {
      // Failed to delete conversation
    }
  };

  const handleEditConversation = (conversationId, currentTitle) => {
    setEditingConversationId(conversationId);
    setEditingTitle(currentTitle);
  };

  const handleSaveConversationTitle = async (conversationId) => {
    if (editingTitle.trim()) {
      await updateConversationTitle(conversationId, editingTitle.trim());
    }
    setEditingConversationId(null);
    setEditingTitle('');
  };

  const handleCancelEdit = () => {
    setEditingConversationId(null);
    setEditingTitle('');
  };

  const handleOpenDeleteConfirm = (conversationId) => {
    setConversationToDelete(conversationId);
    setDeleteConfirmIsOpen(true);
  };

  const handleCancelDelete = () => {
    setDeleteConfirmIsOpen(false);
    setConversationToDelete(null);
    setIsDeleting(false);
  };

  const handleConfirmDelete = async () => {
    if (!conversationToDelete) return;
    
    setIsDeleting(true);
    try {
      await deleteConversation(conversationToDelete);
      setDeleteConfirmIsOpen(false);
      setConversationToDelete(null);
    } catch (error) {
      // Failed to delete conversation
    } finally {
      setIsDeleting(false);
    }
  };

  const getFilteredConversations = () => {
    if (!searchQuery.trim()) {
      return conversations;
    }
    
    const query = searchQuery.toLowerCase();
    return conversations.filter((conv) =>
      conv.title?.toLowerCase().includes(query) ||
      conv.preview?.toLowerCase().includes(query)
    );
  };

  const handleSendMessage = async () => {
    if (!messageInput.trim() || !selectedConversationId) return;

    const userMessageText = messageInput;
    setMessageInput('');

    // Add user message immediately
    const userMessage = {
      id: Date.now(),
      role: 'user',
      content: userMessageText,
    };
    
    // Add loading indicator for bot response
    const loadingMessage = {
      id: Date.now() + 1,
      role: 'assistant',
      content: '...',
      isLoading: true,
    };

    setMessages((prev) => [...prev, userMessage, loadingMessage]);
    setLoading(true);

    try {
      const token = getAuthToken();
      const url = `${API_BASE}/conversations/${selectedConversationId}/send_message/`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ content: userMessageText }),
      });

      if (!response.ok) {
        await response.text();
        
        // Replace loading message with error message
        const errorMessage = {
          id: Date.now() + 2,
          role: 'assistant',
          content: '😊 Oops! Something went wrong on our end. Please try again. If the problem persists, we\'re working on fixing it.',
          isError: true,
        };
        
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === loadingMessage.id ? errorMessage : msg
          )
        );
        
        setLoading(false);
        return;
      }

      const data = await response.json();

      // Create assistant message with ai_response data
      const assistantMsg = {
        id: data.assistant_message?.id || Date.now() + 1,
        role: 'assistant',
        content: data.assistant_message?.content || 'No response',
        ai_response: data.assistant_message?.ai_response || {},
        isLoading: false,
      };

      // Handle pending confirmation if transaction - don't show message yet
      if (data.confirmation && data.confirmation.status === 'pending') {
        // Store the message to display after confirmation
        setPendingAssistantMessage(assistantMsg);
        setPendingConfirmation(data.confirmation);
        // Remove loading message
        setMessages((prev) => prev.filter((msg) => msg.id !== loadingMessage.id));
      } else {
        // Not a transaction - show message immediately
        setMessages((prev) =>
          prev.map((msg) =>
            msg.id === loadingMessage.id ? assistantMsg : msg
          )
        );
      }

      // Update conversation title if it's still "New Conversation"
      const currentConversation = conversations.find(
        (conv) => conv.id === selectedConversationId
      );
      if (currentConversation && currentConversation.title === 'New Conversation') {
        // Shorten the title to 50 characters
        const shortenedTitle = userMessageText.length > 50 
          ? userMessageText.substring(0, 50) + '...' 
          : userMessageText;
        await updateConversationTitle(selectedConversationId, shortenedTitle);
      }
    } catch (error) {
      // Failed to send message
      
      // Replace loading message with error message
      const errorMessage = {
        id: Date.now() + 2,
        role: 'assistant',
        content: '😊 Oops! Something went wrong. Please check your connection and try again.',
        isError: true,
      };
      
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === loadingMessage.id ? errorMessage : msg
        )
      );
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setMessageInput(suggestion);
  };

  const handleConfirmTransaction = async (edits = null) => {
    if (!pendingConfirmation) return;

    try {
      const token = getAuthToken();
      const url = `${API_BASE}/confirmations/${pendingConfirmation.id}/confirm/`;
      
      const payload = {
        confirmed_data: edits || pendingConfirmation.original_data,
        notes: '',
      };
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });
      
      const responseText = await response.text();

      if (!response.ok) {
        
        const errorMessage = {
          id: Date.now(),
          role: 'assistant',
          content: `😊 Failed to confirm transaction. Please try again.`,
          isError: true,
        };
        setMessages((prev) => [...prev, errorMessage]);
        setPendingAssistantMessage(null);
        return;
      }

      try {
        if (responseText) {
          JSON.parse(responseText);
        } else {
        }
      } catch (e) {
        // Failed to parse response
      }
      
      // Add the pending assistant message first (transaction details)
      if (pendingAssistantMessage) {
        setMessages((prev) => [...prev, pendingAssistantMessage]);
      }
      
      // Add confirmation message
      const confirmationMessage = {
        id: Date.now(),
        role: 'assistant',
        content: `✓ Transaction confirmed and saved!`,
      };
      setMessages((prev) => [...prev, confirmationMessage]);
      setPendingConfirmation(null);
      setPendingAssistantMessage(null);
    } catch (error) {
      // Failed to confirm transaction
      
      const errorMessage = {
        id: Date.now(),
        role: 'assistant',
        content: `😊 Failed to confirm transaction. Please check your connection and try again.`,
        isError: true,
      };
      setMessages((prev) => [...prev, errorMessage]);
    }
  };

  const handleRejectTransaction = async () => {
    if (!pendingConfirmation) return;

    try {
      const token = getAuthToken();
      const url = `${API_BASE}/confirmations/${pendingConfirmation.id}/reject/`;
      
      const payload = { notes: 'Rejected by user' };
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        await response.text();
      }

      await response.json();

      const rejectMessage = {
        id: Date.now(),
        role: 'assistant',
        content: 'Transaction rejected. Please describe your transaction again.',
      };
      setMessages((prev) => [...prev, rejectMessage]);
      setPendingConfirmation(null);
      setPendingAssistantMessage(null);
    } catch (error) {
      // Failed to reject transaction
      
      const errorMessage = {
        id: Date.now(),
        role: 'assistant',
        content: `😊 Failed to reject transaction. Please try again.`,
        isError: true,
      };
      setMessages((prev) => [...prev, errorMessage]);
    }
  };

  const handleDeleteMessage = async (messageId) => {
    try {
      const token = getAuthToken();
      await fetch(`${API_BASE}/messages/${messageId}/`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      setMessages((prev) => prev.filter((msg) => msg.id !== messageId));
    } catch (error) {
      // Failed to delete message
    }
  };

  const handleRetryMessage = async (messageId) => {
    try {
      const token = getAuthToken();
      const response = await fetch(`${API_BASE}/messages/${messageId}/retry/`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const data = await response.json();
      
      // Update message with new AI response
      setMessages((prev) =>
        prev.map((msg) =>
          msg.id === messageId
            ? { ...msg, content: data.content, ai_response: data.ai_response }
            : msg
        )
      );
    } catch (error) {
      // Failed to retry message
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleCreateConversation = async () => {
    try {
      const token = getAuthToken();
      const companyId = localStorage.getItem('company_id');
      
      
      if (!token) {
        alert('No authentication token found. Please log in again.');
        return;
      }

      if (!companyId) {
        alert('No company ID found. Please complete your company profile first.');
        return;
      }

      const payload = {
        company: companyId,
        title: 'New Conversation',
      };

      const response = await fetch(`${API_BASE}/conversations/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorText = await response.text();
        alert(`Failed to create conversation: ${response.status}. Error: ${errorText}`);
        return;
      }

      const contentType = response.headers.get('content-type');
      if (!contentType?.includes('application/json')) {
        alert('API returned invalid response. Backend might not be running.');
        return;
      }

      const data = await response.json();

      const newConversation = data;
      setConversations((prev) => [newConversation, ...prev]);
      setSelectedConversationId(newConversation.id);
      setMessages([]);
    } catch (error) {
      alert('Error creating conversation: ' + error.message);
    }
  };

  const selectedConversation = conversations.find(
    (conv) => conv.id === selectedConversationId
  );

  return (
    <AuthenticatedLayout>
      {/* Main container taking full available space */}
      <div className={`flex h-full w-full gap-6 p-6 lg:p-6 md:p-6 sm:p-0 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
        {/* Left Sidebar - Conversations (Desktop Only) */}
        <div className={`hidden lg:flex lg:w-80 lg:flex-col rounded-xl border h-full overflow-hidden ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          {/* Header with New Conversation */}
          <div className={`flex-shrink-0 p-6 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
            <h2 className={`text-xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Messages
            </h2>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className={`absolute left-3 top-3 w-5 h-5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search analytics or conversations..."
                  className={`w-full pl-10 pr-4 py-2 rounded-lg border focus:outline-none focus:border-blue-500 text-sm ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'}`}
                />
              </div>
              <button 
                onClick={handleCreateConversation}
                className="flex-shrink-0 p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Conversations List */}
          <div className="flex-1 overflow-y-auto">
            {conversationsLoading ? (
              <div className={`p-4 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading...</div>
            ) : getFilteredConversations().length === 0 ? (
              <div className={`p-4 text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                {searchQuery ? 'No conversations found' : 'No conversations yet'}
              </div>
            ) : (
              <div className="p-4 space-y-2">
                {getFilteredConversations().map((conv) => (
                  <div
                    key={conv.id}
                    className={`rounded-lg transition-all ${
                      selectedConversationId === conv.id
                        ? darkMode ? 'bg-blue-900 border-2 border-blue-700' : 'bg-blue-50 border-2 border-blue-200'
                        : darkMode ? 'hover:bg-gray-700 border border-transparent' : 'hover:bg-gray-100 border border-transparent'
                    }`}
                  >
                    {editingConversationId === conv.id ? (
                      // Edit Mode
                      <div className="p-4 space-y-3">
                        <input
                          type="text"
                          value={editingTitle}
                          onChange={(e) => setEditingTitle(e.target.value)}
                          className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:border-blue-500 ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'}`}
                          autoFocus
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleSaveConversationTitle(conv.id)}
                            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                          >
                            <Check className="w-4 h-4" />
                            Save
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${darkMode ? 'bg-gray-600 text-white hover:bg-gray-700' : 'bg-gray-300 text-gray-900 hover:bg-gray-400'}`}
                          >
                            <X className="w-4 h-4" />
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      // Normal View
                      <div
                        onClick={() => setSelectedConversationId(conv.id)}
                        className="cursor-pointer p-4 group"
                      >
                        <div className="flex justify-between items-start gap-2 mb-2">
                          <h3 className={`font-semibold text-sm flex-1 line-clamp-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            {conv.title}
                          </h3>
                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEditConversation(conv.id, conv.title);
                              }}
                              className={`p-1 rounded transition-colors ${darkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-300'}`}
                              title="Edit"
                            >
                              <Edit2 className={`w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenDeleteConfirm(conv.id);
                              }}
                              className={`p-1 rounded transition-colors ${darkMode ? 'hover:bg-gray-600' : 'hover:bg-gray-300'}`}
                              title="Delete"
                            >
                              <Trash2 className={`w-4 h-4 ${darkMode ? 'text-red-400' : 'text-red-600'}`} />
                            </button>
                          </div>
                        </div>
                        <p className={`text-xs line-clamp-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {conv.preview}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right Side - Chat Thread (Desktop) OR Conversations List (Mobile) */}
        <div className="flex-1 flex flex-col overflow-hidden lg:gap-6">
          {/* Mobile View - Conversations List OR Chat Thread */}
          <div className="lg:hidden flex-1 flex flex-col overflow-hidden">
            {!selectedConversationId ? (
              // Mobile: Show Conversations List
              <div className={`flex-1 overflow-y-auto ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
                <div className="p-2 lg:p-4 space-y-3">
                  {/* Header */}
                  <div className="mb-3 lg:mb-6">
                    <h2 className={`text-2xl font-bold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>Messages</h2>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Search className={`absolute left-3 top-3 w-5 h-5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                        <input
                          type="text"
                          value={searchQuery}
                          onChange={(e) => setSearchQuery(e.target.value)}
                          placeholder="Search analytics or conversations..."
                          className={`w-full pl-10 pr-4 py-2 rounded-lg border focus:outline-none focus:border-blue-500 text-sm ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'}`}
                        />
                      </div>
                      <button 
                        onClick={handleCreateConversation}
                        className="flex-shrink-0 p-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <Plus className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {/* Conversations */}
                  {conversationsLoading ? (
                    <div className={`text-center ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Loading...</div>
                  ) : getFilteredConversations().length === 0 ? (
                      <div className={`text-center py-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        {searchQuery ? 'No conversations found' : 'No conversations yet'}
                      </div>
                    ) : (
                      getFilteredConversations().map((conv) => (
                      <div
                        key={conv.id}
                        className={`p-4 rounded-lg border transition-shadow ${darkMode ? 'border-gray-700 hover:border-gray-600' : 'border-gray-200 hover:shadow-md'}`}
                      >
                        {editingConversationId === conv.id ? (
                          // Edit Mode (Mobile)
                          <div className="space-y-3">
                            <input
                              type="text"
                              value={editingTitle}
                              onChange={(e) => setEditingTitle(e.target.value)}
                              className={`w-full px-3 py-2 rounded-lg border text-sm focus:outline-none focus:border-blue-500 ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'}`}
                              autoFocus
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleSaveConversationTitle(conv.id)}
                                className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                              >
                                <Check className="w-4 h-4" />
                                Save
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${darkMode ? 'bg-gray-600 text-white hover:bg-gray-700' : 'bg-gray-300 text-gray-900 hover:bg-gray-400'}`}
                              >
                                <X className="w-4 h-4" />
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          // Normal View (Mobile)
                          <>
                            <div
                              onClick={() => setSelectedConversationId(conv.id)}
                              className="cursor-pointer"
                            >
                              <div className="flex gap-3">
                                <div className="flex-shrink-0">
                                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold text-lg">
                                    {conv.title?.charAt(0)}
                                  </div>
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex justify-between items-start gap-2">
                                    <h3 className={`font-semibold text-sm line-clamp-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                                      {conv.title}
                                    </h3>
                                  </div>
                                  <p className={`text-xs mt-1 line-clamp-2 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                    {conv.preview}
                                  </p>
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2 mt-3 pt-3 border-t" style={{ borderTopColor: darkMode ? '#374151' : '#e5e7eb' }}>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleEditConversation(conv.id, conv.title);
                                }}
                                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${darkMode ? 'bg-gray-700 text-white hover:bg-gray-600' : 'bg-gray-200 text-gray-900 hover:bg-gray-300'}`}
                              >
                                <Edit2 className="w-4 h-4" />
                                Edit
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenDeleteConfirm(conv.id);
                                }}
                                className={`flex-1 flex items-center justify-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${darkMode ? 'bg-red-900 text-red-100 hover:bg-red-800' : 'bg-red-100 text-red-900 hover:bg-red-200'}`}
                              >
                                <Trash2 className="w-4 h-4" />
                                Delete
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            ) : (
              // Mobile: Show Chat Thread
              <div className={`flex flex-col h-full overflow-hidden ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
                {/* Chat Header with Back Button */}
                <div className={`flex-shrink-0 border-b p-2 lg:p-4 ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setSelectedConversationId(null)}
                      className={`p-1 rounded transition-colors ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                    >
                      <X className={`w-6 h-6 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} />
                    </button>
                    <div className="flex-1 min-w-0">
                      <h2 className={`text-lg font-bold line-clamp-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        Accurix Advisor
                      </h2>
                      <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        AI-Driven Financial Intelligence
                      </p>
                    </div>
                  </div>
                </div>

                {/* Messages Area */}
                <div className={`flex-1 overflow-y-auto p-2 lg:p-4 space-y-4 pb-24 lg:pb-0 ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
                  {messages.length === 0 ? (
                    <div className="flex items-center justify-center h-full">
                      <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Start a conversation with Accurix Advisor
                      </p>
                    </div>
                  ) : (
                    <>
                      {messages.filter(msg => msg && msg.role).map((message) => {
                        const isTransaction = isTransactionMessage(message);
                        const isError = isErrorMessage(message);
                        
                        return (
                          <div key={message.id} className="group flex">
                            <div
                              className={`flex w-full ${
                                message.role === 'user' ? 'justify-end' : 'justify-start'
                              }`}
                            >
                              <div
                                className={`max-w-lg px-4 py-3 rounded-lg text-sm ${
                                  message.role === 'user'
                                    ? 'bg-blue-600 text-white rounded-br-none'
                                    : isError
                                    ? darkMode ? 'bg-red-900/20 text-red-200 border border-red-700 rounded-bl-none' : 'bg-red-100 text-red-900 border border-red-300 rounded-bl-none'
                                    : darkMode ? 'bg-gray-800 text-gray-100 rounded-bl-none border border-gray-700' : 'bg-gray-100 text-gray-900 rounded-bl-none'
                                }`}
                              >
                                {message.isLoading ? (
                                  <div className="flex gap-1">
                                    <div className={`w-2 h-2 rounded-full animate-bounce ${darkMode ? 'bg-gray-400' : 'bg-gray-600'}`} style={{ animationDelay: '0ms' }}></div>
                                    <div className={`w-2 h-2 rounded-full animate-bounce ${darkMode ? 'bg-gray-400' : 'bg-gray-600'}`} style={{ animationDelay: '150ms' }}></div>
                                    <div className={`w-2 h-2 rounded-full animate-bounce ${darkMode ? 'bg-gray-400' : 'bg-gray-600'}`} style={{ animationDelay: '300ms' }}></div>
                                  </div>
                                ) : isTransaction ? (
                                  // Transaction message with formatted data
                                  <div className="space-y-2">
                                    <div dangerouslySetInnerHTML={{ __html: message.content }} className="whitespace-pre-wrap break-words" />
                                    
                                    {message.ai_response && (
                                      <div className="mt-3 pt-3 border-t border-gray-600 dark:border-gray-600">
                                        <div className="flex flex-wrap gap-2">
                                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(message.ai_response.category)}`}>
                                            {message.ai_response.category}
                                          </span>
                                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${message.role === 'user' ? 'bg-blue-700 text-white' : message.ai_response.type === 'sale' ? 'bg-green-700 text-white' : 'bg-orange-700 text-white'}`}>
                                            {message.ai_response.type === 'sale' ? '💰 Sale' : '💸 Expense'}
                                          </span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <p className="whitespace-pre-wrap break-words" dangerouslySetInnerHTML={{ __html: message.content }} />
                                )}
                              </div>
                            </div>

                            {/* Message Actions */}
                            {message.role === 'assistant' && !message.isLoading && !message.isError && (
                              <div className="flex gap-2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  onClick={() => handleRetryMessage(message.id)}
                                  className={`p-1 rounded transition-colors ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
                                  title="Retry"
                                >
                                  <RotateCcw className={`w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                                </button>
                                <button
                                  onClick={() => handleDeleteMessage(message.id)}
                                  className={`p-1 rounded transition-colors ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
                                  title="Delete"
                                >
                                  <Trash2 className={`w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                                </button>
                              </div>
                            )}
                          </div>
                        );
                      })}

                      {/* Pending Confirmation UI */}
                      {pendingConfirmation && (
                        <div className={`border-2 rounded-lg p-3 space-y-3 ${darkMode ? 'bg-blue-900 border-blue-700' : 'bg-blue-50 border-blue-200'}`}>
                          <h3 className={`font-semibold text-sm ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            Please confirm this transaction
                          </h3>

                          <div className={`p-3 rounded border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-blue-200'}`}>
                            {pendingConfirmation.original_data && (
                              <>
                                {Object.entries(pendingConfirmation.original_data).map(
                                  ([key, value]) => (
                                    <div key={key} className="flex justify-between text-xs">
                                      <span className={`capitalize ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                        {key}:
                                      </span>
                                      <span className={`font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                                        {typeof value === 'object'
                                          ? JSON.stringify(value)
                                          : String(value)}
                                      </span>
                                    </div>
                                  )
                                )}
                              </>
                            )}
                          </div>

                          <div className="flex gap-2">
                            <button
                              onClick={() => handleConfirmTransaction()}
                              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
                            >
                              <Check className="w-4 h-4" />
                              Confirm
                            </button>
                            <button
                              onClick={handleRejectTransaction}
                              className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
                            >
                              <XCircle className="w-4 h-4" />
                              Reject
                            </button>
                          </div>
                        </div>
                      )}

                      <div ref={scrollRef} />
                    </>
                  )}
                </div>

                {/* Suggestion Bubbles - Only show when no messages */}
                {messages.length === 0 && (
                  <div className={`flex-shrink-0 px-4 py-2 flex flex-col gap-2 items-end ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
                    <button
                      onClick={() => handleSuggestionClick('I sold 50 bags for 50000')}
                      className={`max-w-xs px-3 py-2 rounded-full text-xs font-medium transition-all hover:shadow-md border-2 ${darkMode ? 'border-blue-500/50 bg-blue-600/20 text-blue-300 hover:bg-blue-600/30' : 'border-blue-400/50 bg-blue-400/15 text-blue-600 hover:bg-blue-400/25'}`}
                    >
                      💡 I sold 50 bags for 50000
                    </button>
                    <button
                      onClick={() => handleSuggestionClick('I bought 23 staplers for 40000')}
                      className={`max-w-xs px-3 py-2 rounded-full text-xs font-medium transition-all hover:shadow-md border-2 ${darkMode ? 'border-blue-500/50 bg-blue-600/20 text-blue-300 hover:bg-blue-600/30' : 'border-blue-400/50 bg-blue-400/15 text-blue-600 hover:bg-blue-400/25'}`}
                    >
                      💡 I bought 23 staplers for 40000
                    </button>
                  </div>
                )}

                {/* Input Area */}
                <div className={`flex-shrink-0 border-t p-2 lg:p-4 fixed lg:static bottom-0 left-0 right-0 lg:bottom-auto lg:left-auto lg:right-auto ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={messageInput}
                      onChange={(e) => setMessageInput(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Ask about your fiscal trends..."
                      className={`flex-1 px-3 py-2 rounded-lg border focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all text-sm resize-none ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-300 bg-white text-gray-900'}`}
                      disabled={loading}
                    />
                    <Button
                      variant="primary"
                      onClick={handleSendMessage}
                      disabled={loading || !messageInput.trim() || !selectedConversationId}
                      className="flex-shrink-0 px-4 py-2"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Desktop View - Chat Thread */}
          <div className={`hidden lg:flex lg:flex-col lg:flex-1 lg:overflow-hidden ${darkMode ? 'bg-gray-900' : 'bg-gray-50'}`}>
            {selectedConversation ? (
              <div className={`flex flex-col rounded-xl border h-full overflow-hidden shadow-sm ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
                {/* Chat Header */}
                <div className={`flex-shrink-0 border-b p-6 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <div className="min-w-0">
                    <h2 className={`text-2xl font-bold line-clamp-1 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                      Accurix Advisor
                    </h2>
                    <p className={`text-sm mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      AI-Driven Financial Intelligence
                    </p>
                  </div>
                </div>

                {/* Messages Area */}
                <div className={`flex-1 overflow-y-auto p-6 space-y-4 ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
                  {messages.length === 0 ? (
                    <div className="flex items-center justify-center h-full">
                      <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        Start a conversation with Accurix Advisor
                      </p>
                    </div>
                  ) : (
                    <>
                      {messages.filter(msg => msg && msg.role).map((message) => {
                        const isTransaction = isTransactionMessage(message);
                        const isError = isErrorMessage(message);
                        
                        return (
                          <div key={message.id} className="group flex">
                            <div
                              className={`flex w-full ${
                                message.role === 'user' ? 'justify-end' : 'justify-start'
                              }`}
                            >
                              <div
                                className={`max-w-xl px-4 py-3 rounded-lg text-base ${
                                  message.role === 'user'
                                    ? 'bg-blue-600 text-white rounded-br-none'
                                    : isError
                                    ? darkMode ? 'bg-red-900/20 text-red-200 border border-red-700 rounded-bl-none' : 'bg-red-100 text-red-900 border border-red-300 rounded-bl-none'
                                    : darkMode ? 'bg-gray-800 text-gray-100 rounded-bl-none border border-gray-700' : 'bg-gray-100 text-gray-900 rounded-bl-none'
                                }`}
                              >
                                {message.isLoading ? (
                                  <div className="flex gap-1">
                                    <div className={`w-2 h-2 rounded-full animate-bounce ${darkMode ? 'bg-gray-400' : 'bg-gray-600'}`} style={{ animationDelay: '0ms' }}></div>
                                    <div className={`w-2 h-2 rounded-full animate-bounce ${darkMode ? 'bg-gray-400' : 'bg-gray-600'}`} style={{ animationDelay: '150ms' }}></div>
                                    <div className={`w-2 h-2 rounded-full animate-bounce ${darkMode ? 'bg-gray-400' : 'bg-gray-600'}`} style={{ animationDelay: '300ms' }}></div>
                                  </div>
                                ) : isTransaction ? (
                                  // Transaction message with formatted data
                                  <div className="space-y-2">
                                    <div dangerouslySetInnerHTML={{ __html: message.content }} className="whitespace-pre-wrap break-words" />
                                    
                                    {message.ai_response && (
                                      <div className="mt-3 pt-3 border-t border-gray-600 dark:border-gray-600">
                                        <div className="flex flex-wrap gap-2">
                                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getCategoryColor(message.ai_response.category)}`}>
                                            {message.ai_response.category}
                                          </span>
                                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${message.role === 'user' ? 'bg-blue-700 text-white' : message.ai_response.type === 'sale' ? 'bg-green-700 text-white' : 'bg-orange-700 text-white'}`}>
                                            {message.ai_response.type === 'sale' ? '💰 Sale' : '💸 Expense'}
                                          </span>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                  <p className="whitespace-pre-wrap break-words" dangerouslySetInnerHTML={{ __html: message.content }} />
                                )}
                              </div>
                            </div>

                            {/* Message Actions */}
                            {message.role === 'assistant' && !message.isLoading && !message.isError && (
                              <div className="flex gap-2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button
                                  onClick={() => handleRetryMessage(message.id)}
                                  className={`p-1 rounded transition-colors ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
                                  title="Retry"
                              >
                                <RotateCcw className={`w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                              </button>
                              <button
                                onClick={() => handleDeleteMessage(message.id)}
                                className={`p-1 rounded transition-colors ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'}`}
                                title="Delete"
                              >
                                <Trash2 className={`w-4 h-4 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`} />
                              </button>
                            </div>
                          )}
                        </div>
                      );
                      })}

                      {/* Pending Confirmation UI */}
                      {pendingConfirmation && (
                        <div className={`border-2 rounded-lg p-4 space-y-4 ${darkMode ? 'bg-blue-900 border-blue-700' : 'bg-blue-50 border-blue-200'}`}>
                          <h3 className={`font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                            Please confirm this transaction
                          </h3>

                          <div className={`p-4 rounded border ${darkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-blue-200'}`}>
                            {pendingConfirmation.original_data && (
                              <>
                                {Object.entries(pendingConfirmation.original_data).map(
                                  ([key, value]) => (
                                    <div key={key} className="flex justify-between">
                                      <span className={`capitalize ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                        {key}:
                                      </span>
                                      <span className={`font-semibold ${darkMode ? 'text-gray-100' : 'text-gray-900'}`}>
                                        {typeof value === 'object'
                                          ? JSON.stringify(value)
                                          : String(value)}
                                      </span>
                                    </div>
                                  )
                                )}
                              </>
                            )}
                          </div>

                          <div className="flex gap-2">
                            <button
                              onClick={() => handleConfirmTransaction()}
                              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                            >
                              <Check className="w-4 h-4" />
                              Confirm
                            </button>
                            <button
                              onClick={handleRejectTransaction}
                              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                            >
                              <XCircle className="w-4 h-4" />
                              Reject
                            </button>
                          </div>
                        </div>
                      )}

                      <div ref={scrollRef} />
                    </>
                  )}
                </div>

                {/* Suggestion Bubbles - Desktop Only */}
                {messages.length === 0 && (
                  <div className={`flex-shrink-0 px-6 py-4 flex flex-col gap-2 items-end ${darkMode ? 'bg-gray-900' : 'bg-white'}`}>
                    <button
                      onClick={() => handleSuggestionClick('I sold 50 bags for 50000')}
                      className={`max-w-sm px-4 py-2 rounded-full text-sm font-medium transition-all hover:shadow-md border-2 ${darkMode ? 'border-blue-500/50 bg-blue-600/20 text-blue-300 hover:bg-blue-600/30' : 'border-blue-400/50 bg-blue-400/15 text-blue-600 hover:bg-blue-400/25'}`}
                    >
                      💡 I sold 50 bags for 50000
                    </button>
                    <button
                      onClick={() => handleSuggestionClick('I bought 23 staplers for 40000')}
                      className={`max-w-sm px-4 py-2 rounded-full text-sm font-medium transition-all hover:shadow-md border-2 ${darkMode ? 'border-blue-500/50 bg-blue-600/20 text-blue-300 hover:bg-blue-600/30' : 'border-blue-400/50 bg-blue-400/15 text-blue-600 hover:bg-blue-400/25'}`}
                    >
                      💡 I bought 23 staplers for 40000
                    </button>
                  </div>
                )}

                {/* Input Area */}
                <div className={`flex-shrink-0 border-t p-6 ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
                  <div className="flex gap-3">
                    <input
                      type="text"
                      value={messageInput}
                      onChange={(e) => setMessageInput(e.target.value)}
                      onKeyPress={handleKeyPress}
                      placeholder="Ask about your fiscal trends..."
                      className={`flex-1 px-4 py-3 rounded-lg border focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all ${darkMode ? 'border-gray-600 bg-gray-700 text-white placeholder-gray-400' : 'border-gray-300 bg-white text-gray-900'}`}
                      disabled={loading}
                    />
                    <Button
                      variant="primary"
                      onClick={handleSendMessage}
                      disabled={loading || !messageInput.trim() || !selectedConversationId}
                      className="flex-shrink-0 px-6 py-3"
                    >
                      <Send className="w-5 h-5" />
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center flex-1">
                <p className={`${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>Select a conversation to start chatting</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmIsOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className={`rounded-lg shadow-lg max-w-md w-full mx-4 ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`flex justify-between items-center p-6 border-b ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <h2 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                Delete Conversation
              </h2>
              <button
                onClick={handleCancelDelete}
                disabled={isDeleting}
                className={`transition-colors ${darkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'} disabled:opacity-50`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-6">
              <p className={`${darkMode ? 'text-gray-300' : 'text-gray-700'} mb-6`}>
                Are you sure you want to delete this conversation? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={handleCancelDelete}
                  disabled={isDeleting}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${darkMode ? 'bg-gray-700 text-white hover:bg-gray-600 disabled:bg-gray-700' : 'bg-gray-200 text-gray-900 hover:bg-gray-300 disabled:bg-gray-200'} disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmDelete}
                  disabled={isDeleting}
                  className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors text-white ${isDeleting ? 'bg-red-500 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'} disabled:opacity-50`}
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AuthenticatedLayout>
  );
};
